import {
  CheckoutBuilderComponentTypes,
  CheckoutBuilderDeviceTypes,
} from '../../sections/@checkout-builder/constants';
import {
  applyExitPopupToConfig,
  keepServerExitPopup,
  skipsExitPopupSave,
} from '../../sections/@dashboard/product-exit-popup/exitPopupConfig';
import { getCheckout, updateCheckoutService } from '../product-checkout';
import { uploadImage } from '../product-gallery';

export const saveCheckout = async ({ id, product: { id: productID }, ...checkout }) => {
  const checkImageAndUpload = async (value) => {
    if (value instanceof File) {
      return uploadImage({ file: value, productID });
    }
    return value;
  };

  const components = {
    [CheckoutBuilderComponentTypes.image.id]: ['image'],
    [CheckoutBuilderComponentTypes.header.id]: ['backgroundImage', 'productImage'],
    [CheckoutBuilderComponentTypes.testimonial.id]: ['avatar'],
  };

  // Antes do upload: se a leitura falhar, nenhuma imagem sobe sem checkout para usá-la.
  const { config: serverConfig } = await getCheckout({ id });

  const config = {
    mobile: checkout.config.mobile,
    desktop: checkout.config.desktop,
  };

  const checkImages = async (device) => {
    config[device].settings = {
      ...config[device].settings,
      background: {
        ...config[device].settings.background,
        image: await checkImageAndUpload(
          config[device].settings.background.image?.file || config[device].settings.background.image
        ),
      },
    };

    config[device].rows = await Promise.all(
      config[device].rows.map(async (row) => ({
        ...row,
        columns: await Promise.all(
          row.columns.map(async (column) => ({
            ...column,
            type: column?.type || 'column',
            components: await Promise.all(
              column.components.map(async (component) => {
                if (component.type in components) {
                  await Promise.all(
                    Object.keys(component.attributes).map(async (attribute) => {
                      component.attributes[attribute] = await checkImageAndUpload(
                        component.attributes[attribute]
                      );
                    })
                  );
                }
                return component;
              })
            ),
          }))
        ),
      }))
    );
  };

  await Promise.all(Object.keys(CheckoutBuilderDeviceTypes).map(checkImages));

  const configToSave = keepServerExitPopup(config, serverConfig);

  return updateCheckoutService({
    id,
    config: configToSave,
    default: checkout.default,
    name: checkout.name,
    offers: checkout.offers?.map((offer) => offer.id),
  }).then(() => configToSave);
};

// O checkout público serve o config do checkout padrão quando o deste é nulo (`Checkout.getConfig`
// no back), mas o endpoint do builder devolve o nulo cru. Partir do padrão mantém o layout e o
// popup que o comprador já vê.
export const getExitPopupCheckout = async ({ id, defaultCheckoutId }) => {
  const checkout = await getCheckout({ id });

  if (checkout.config || !defaultCheckoutId || defaultCheckoutId === id) {
    return { ...checkout, inheritsDefaultConfig: false };
  }

  const { config } = await getCheckout({ id: defaultCheckoutId });
  return { ...checkout, config, inheritsDefaultConfig: true };
};

export const saveCheckoutExitPopup = async ({
  id,
  productId,
  defaultCheckoutId,
  attributesByDevice,
}) => {
  // Lê o checkout na hora de salvar para não regravar o layout com uma cópia carregada antes
  // de alguém salvar pelo builder.
  const checkout = await getExitPopupCheckout({ id, defaultCheckoutId });

  // `skipped` separa o "nada gravado" do sucesso: sem ele a tela anunciaria o save e trocaria o que
  // o produtor digitou pelo checkout lido.
  if (skipsExitPopupSave(checkout, attributesByDevice)) {
    return { ...checkout, skipped: true };
  }

  // Com a mesma configuração nos dois dispositivos, o File é o mesmo objeto: sobe uma vez só, senão
  // a galeria do produto ganha uma cópia órfã a cada save.
  const uploads = new Map();
  const upload = (file) => {
    if (!uploads.has(file)) uploads.set(file, uploadImage({ file, productID: productId }));
    return uploads.get(file);
  };

  const uploadedAttributes = Object.fromEntries(
    await Promise.all(
      Object.entries(attributesByDevice).map(async ([device, attributes]) => [
        device,
        {
          ...attributes,
          image:
            attributes.image instanceof File ? await upload(attributes.image) : attributes.image,
        },
      ])
    )
  );

  const config = applyExitPopupToConfig(checkout.config, uploadedAttributes);

  return updateCheckoutService({
    id,
    config,
    default: checkout.default,
    name: checkout.name,
    offers: checkout.offers?.map((offer) => offer.id),
  }).then(() => ({ ...checkout, config, inheritsDefaultConfig: false }));
};
